import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute, Data, NavigationEnd, Router} from "@angular/router";
import {
  ADMINISTRATOR_LIST, LIST_OF_DISCOUNTS,
  LIST_OF_REQUESTS, CATEGORIES_LIST,
  ORGANISER_EVENTS,
  ORGANISER_LIST,
  ORGANISER_PROFILE,
  USER_LIST, ACCOUNT_STATEMENTS, MY_TRACEY, MEASUREMENTS_LIST, LEAGUES
} from "src/app/app-routing.module";
import {switchMap} from 'rxjs';
import {AbstractComponent} from "../abstract.component";
import {
  AuthenticationService,
  EventService,
  Features,
  Permission,
  PermissionService,
  RaceService,
  RequestService
} from "@services";
import {RequestFilter, RequestStatus} from "@model";
import {environment} from "@environment";

enum AdminMenuItem {
  DASHBOARD = "DASHBOARD",
  EVENTS = "EVENTS",
  LEAGUE = "LEAGUE",
  LIST_OF_DISCOUNTS = "LIST_OF_DISCOUNTS",
  USERS = "USERS",
  PROFILE = "PROFILE",
  ORGANISERS = "ORGANISERS",
  CATEGORIES_LIST = "CATEGORIES_LIST",
  LIST_OF_REQUESTS = "LIST_OF_REQUESTS",
  ADMINISTRATORS = "ADMINISTRATORS",
  ACCOUNT_STATEMENTS = "ACCOUNT_STATEMENTS",
  MESSAGES = "MESSAGES",
  MEASUREMENTS = "MEASUREMENTS"
}

class MenuItem {
  constructor(public key: AdminMenuItem, public labelKey: string, public icon: string, public navigationRoute?: string,
              public navigationQueryParams?: {}, public isActive?: boolean, public hasPermission?: boolean) {
  }
}

@Component({
    selector: 'app-admin-menu',
    templateUrl: './admin-menu.component.html',
    styleUrls: ['./admin-menu.component.scss'],
    standalone: false
})
export class AdminMenuComponent extends AbstractComponent implements OnInit {
  @Input() isMobileMenuActive: boolean = false;
  @Output() closeMobileMenu: EventEmitter<any> = new EventEmitter();
  numberOfRequests: number | undefined;
  items: MenuItem[] = [];

  constructor(private router: Router, private route: ActivatedRoute, private requestService: RequestService,
              private authenticationService: AuthenticationService, private permissionService: PermissionService,
              private eventService: EventService, private raceService: RaceService) {
    super();
  }

  ngOnInit() {
    this.initMenuItems();
    this.subscriptions.push(this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.setActiveItem(val.url);
      }
    }));

    this.subscriptions.push(this.route.data.subscribe(({requests}: Data): void => {
      this.numberOfRequests = (requests?.length ?? 0);
    }));

    this.subscriptions.push(
      this.requestService.fetchNewRequests.pipe(
        switchMap(() => {
          let filter: RequestFilter = new RequestFilter(RequestStatus.ON_HOLD);
          return this.requestService.fetchRequestsCount(filter);
        }),
      ).subscribe((value: number) => this.numberOfRequests = value));
  }

  private initMenuItems() {
    this.items = [];
    Object.values(AdminMenuItem).forEach((route: AdminMenuItem): void => {
      const menuItem: MenuItem = this.adminMenuItemsMap[route];
      if (menuItem && this.checkPermission(menuItem.key)) {
        if (menuItem.navigationRoute && this.router.url.includes(menuItem.navigationRoute)) {
          menuItem.isActive = true;
        }
        this.items.push(menuItem);
      }
    });
  }

  setActiveItem(route: string): void {
    this.items.forEach((item: MenuItem): void => {
      item.isActive = !!(item.navigationRoute && route.includes(item.navigationRoute));
    })
  }

  checkPermission(listItem: AdminMenuItem): boolean {
    switch (listItem) {
      case AdminMenuItem.DASHBOARD:
        return false;
      case AdminMenuItem.EVENTS:
        return true;
      case AdminMenuItem.LEAGUE:
        return this.permissionService.checkPermission(Features.LeaguesList, Permission.Edit);
      case AdminMenuItem.MEASUREMENTS:
        return true;
      case AdminMenuItem.CATEGORIES_LIST:
        return this.permissionService.checkPermission(Features.Categories, Permission.Edit);
      case AdminMenuItem.LIST_OF_DISCOUNTS:
        return this.permissionService.checkPermission(Features.DiscountsList, Permission.Edit);
      case AdminMenuItem.PROFILE:
        return this.permissionService.checkPermission(Features.OrganiserProfile, Permission.Edit);
      case AdminMenuItem.ORGANISERS:
        return this.permissionService.checkPermission(Features.OrganisersList, Permission.Edit);
      case AdminMenuItem.LIST_OF_REQUESTS:
        return this.permissionService.checkPermission(Features.RequestsList, Permission.Edit);
      case AdminMenuItem.ADMINISTRATORS:
        return this.permissionService.checkPermission(Features.Administrators, Permission.Edit);
      case AdminMenuItem.ACCOUNT_STATEMENTS:
        return this.permissionService.checkPermission(Features.AccountStatements, Permission.Edit);
      case AdminMenuItem.USERS:
        return this.permissionService.checkPermission(Features.Users, Permission.Edit);
      default:
        return false;
    }
  }

  private onClickOnMenuItem() {
    this.eventService.currentEvent.next(undefined);
    this.raceService.currentRace.next(undefined);
    this.closeMobileMenu.emit();
  }

  onClickToLogoImage(): void {
    this.goToOrganiserEvents();
  }

  goToOrganiserEvents = () => {
    this.onClickOnMenuItem();
    this.router.navigate([ORGANISER_EVENTS]);
  }

  goToUserModule = () => {
    this.onClickOnMenuItem();
    this.router.navigate([MY_TRACEY]);
  }

  menuItemClicked(route?: string, queryParams?: {}) {
    if (route) {
      this.onClickOnMenuItem();
      if (queryParams) {
        this.router.navigate([route], {queryParams: {...queryParams}});
      } else {
        this.router.navigate([route])
      }
    }
  }

  logout(): void {
    this.onClickOnMenuItem();
    this.authenticationService.logout();
  }

  onSidebarHover(isHovered: boolean): void {
    if (window.innerWidth <= 639) {
      return;
    }

    document.documentElement.style.setProperty(
      '--admin-sidebar-width',
      isHovered ? '288px' : 'var(--admin-sidebar-collapsed-width)'
    );

    if (isHovered) {
      document.documentElement.classList.add('admin-sidebar-expanded');
    } else {
      document.documentElement.classList.remove('admin-sidebar-expanded');
    }
  }

  adminMenuItemsMap: { [key: string]: MenuItem } = {
/*
    [AdminMenuItem.DASHBOARD]: new MenuItem(AdminMenuItem.DASHBOARD, 'app.common.dashboard', 'fas  fa-home', ORGANISER_DASHBOARD),
*/
    [AdminMenuItem.EVENTS]: new MenuItem(AdminMenuItem.EVENTS, 'app.common.events', 'fas fa-calendar-alt', ORGANISER_EVENTS),
    ...(environment.isLeagueVisible && {
      [AdminMenuItem.LEAGUE]: new MenuItem(
          AdminMenuItem.LEAGUE,
          'app.common.league.series',
          'fas fa-running',
          LEAGUES
      )
    }),
    [AdminMenuItem.LIST_OF_DISCOUNTS]: new MenuItem(AdminMenuItem.LIST_OF_DISCOUNTS, 'app.common.discounts.management', 'fas fa-tags', LIST_OF_DISCOUNTS),
    [AdminMenuItem.USERS]: new MenuItem(AdminMenuItem.USERS, 'app.common.users', 'fas fa-users', USER_LIST),
    [AdminMenuItem.PROFILE]: new MenuItem(AdminMenuItem.PROFILE, 'app.common.profile', 'fa fa-user', ORGANISER_PROFILE),
    [AdminMenuItem.ORGANISERS]: new MenuItem(AdminMenuItem.ORGANISERS, 'app.common.organizers', 'fa fa-clipboard-list', ORGANISER_LIST),
    [AdminMenuItem.MEASUREMENTS]: new MenuItem(AdminMenuItem.MEASUREMENTS, 'app.common.measurements', 'fa' +
      ' fa-clipboard-list', MEASUREMENTS_LIST),
    [AdminMenuItem.CATEGORIES_LIST]: new MenuItem(AdminMenuItem.CATEGORIES_LIST, 'app.common.categories.list', 'fa fa-list', CATEGORIES_LIST),
    [AdminMenuItem.LIST_OF_REQUESTS]: new MenuItem(AdminMenuItem.LIST_OF_REQUESTS, 'app.common.list.of.requests', 'fa fa-globe', LIST_OF_REQUESTS, {status: RequestStatus.ON_HOLD}),
    [AdminMenuItem.ADMINISTRATORS]: new MenuItem(AdminMenuItem.ADMINISTRATORS, 'app.common.administrators', 'fa' +
      ' fa-user-tie', ADMINISTRATOR_LIST),
    [AdminMenuItem.ACCOUNT_STATEMENTS]: new MenuItem(AdminMenuItem.ACCOUNT_STATEMENTS, 'app.common.accountStatements', 'fa' +
      ' fa-money-bill-1', ACCOUNT_STATEMENTS)
  };

  protected readonly AdminMenuItem = AdminMenuItem;
}
